import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { recordFor, formatDate } from "@/data/content";
import { MapPin, Printer } from "lucide-react";

export default function Schedule() {
  const { site } = useSite();
  const games = [...site.games]
    .filter((g) => g.level !== "JV")
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const v = recordFor(games, "Varsity");

  return (
    <div data-testid="schedule-page">
      <PageHeader
        eyebrow="Season Schedule"
        title="Schedule & Results"
        subtitle={site.content.subtitle_schedule}
        testid="schedule-header"
      />

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="bg-[#0F4C2A] text-white rounded-lg p-5 text-center">
                <div className="font-stat text-3xl font-bold" data-testid="varsity-record">{v.w}-{v.l}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/70 mt-1">Varsity Record</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5 text-center">
                <div className="font-stat text-3xl font-bold text-[#0F4C2A]" data-testid="games-played">{games.length}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">Games Scheduled</div>
              </div>
              <button
                onClick={() => window.print()}
                data-testid="print-schedule-button"
                className="bg-white border border-slate-200 rounded-lg p-5 text-center hover:border-[#0F4C2A]/40 transition-colors card-lift"
              >
                <Printer className="mx-auto text-[#D97706]" size={26} />
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-2 font-bold">Print Schedule</div>
              </button>
            </div>
          </Reveal>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="schedule-table-container">
            <div className="overflow-x-auto">
              <table className="w-full text-left min-w-[640px]">
                <thead>
                  <tr className="bg-[#0F4C2A] text-white text-[11px] uppercase tracking-[0.15em]">
                    <th className="px-5 py-3.5 font-bold">Date</th>
                    <th className="px-5 py-3.5 font-bold">Opponent</th>
                    <th className="px-5 py-3.5 font-bold">Time</th>
                    <th className="px-5 py-3.5 font-bold">Location</th>
                    <th className="px-5 py-3.5 font-bold text-right">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((g) => (
                    <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`schedule-row-${g.id}`}>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-900 whitespace-nowrap">{formatDate(g.date)}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${g.ha === "Home" ? "bg-[#0F4C2A]/10 text-[#0F4C2A]" : "bg-slate-200 text-slate-600"}`}>
                            {g.ha === "Home" ? "vs" : "at"}
                          </span>
                          <span className="text-sm font-semibold text-slate-900">{g.opponent}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600 whitespace-nowrap">{g.time}</td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-slate-400 shrink-0" />{g.location}</span>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        {g.wl === "W" || g.wl === "L" ? (
                          <span className={`inline-block font-stat font-bold text-sm px-3 py-1 rounded-sm ${g.wl === "W" ? "bg-green-100 text-[#166534]" : "bg-red-100 text-[#991B1B]"}`} data-testid={`result-${g.id}`}>
                            {g.wl} {g.score}
                          </span>
                        ) : (
                          <span className="inline-block font-stat font-bold text-sm px-3 py-1 rounded-sm bg-[#F59E0B]/15 text-[#D97706]" data-testid={`result-${g.id}`}>
                            TBD
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {games.length === 0 && <p className="p-10 text-center text-slate-500" data-testid="schedule-empty-state">No varsity games scheduled yet — check back soon.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}
